import { SignInOpts, SignUpParams } from "@aws-amplify/auth/lib-esm/types/Auth";
import {
  Authenticator,
  CheckboxField,
  Image,
  Text,
  Theme,
  ThemeProvider,
  useTheme,
  View,
} from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { Box, ChakraProvider, Link, theme } from "@chakra-ui/react";
import * as Sentry from "@sentry/react";
import { Amplify, Auth, I18n } from "aws-amplify";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { developmentInitAccount } from "./api/api";
import Billing from "./components/billing";
import { AppStateProvider } from "./components/contexts/app";
import Home from "./components/home/home";
import Medical from "./components/medical";
import Navbar from "./components/nav/navbar";
import Settings from "./components/settings/settings";
import SubcriptionSuccess from "./components/settings/subscription-success";
import { Actions, Analytics, Features } from "./components/shared/analytics";
import "./index.css";
import { capture } from "./shared/capture";
import { isDevEnv } from "./shared/util";

Amplify.configure({
  Auth: {
    userPoolId: process.env.REACT_APP_USER_POOL_ID,
    region: process.env.REACT_APP_AWS_REGION,
    userPoolWebClientId: process.env.REACT_APP_USER_POOL_WEB_CLIENT_ID,
  },
});

const logo = "logo.png";

const amplifyTheme: Theme = {
  name: "custom-theme",
  tokens: {
    colors: {
      font: {
        primary: { value: "black" },
        secondary: { value: "#879ced" },
      },
      brand: {
        primary: { "80": { value: "#748df0" } },
      },
    },
  },
};

I18n.putVocabulariesForLanguage("en", {
  "Given Name": "First Name",
  "Family Name": "Last Name",
  Website: "Company Website",
});

const components = {
  SignUp: {
    FormFields() {
      return (
        <>
          {/* Re-use default `Authenticator.SignUp.FormFields` */}
          <Authenticator.SignUp.FormFields />

          {/* Append & require Terms of Service field to sign up  */}
          <CheckboxField
            name="accepted_tos"
            value="true"
            label={
              <Box>
                I have read and agree to the Metriport{" "}
                <Link
                  href="https://www.metriport.com/terms-of-service"
                  textDecoration={"underline"}
                  isExternal
                >
                  Terms of Service
                </Link>
              </Box>
            }
            required
          />
        </>
      );
    },
  },

  Header() {
    const { tokens } = useTheme();

    return (
      <View textAlign="center" padding={tokens.space.large}>
        <Image marginTop={"40%"} alt="Metriport logo" width={"70%"} src={logo} />
      </View>
    );
  },
  Footer() {
    const { tokens } = useTheme();

    return (
      <View textAlign="center" padding={tokens.space.large}>
        <Text color={tokens.colors.neutral[80]}>&copy; All Rights Reserved</Text>
      </View>
    );
  },
};

const services = {
  async handleSignIn(formData: SignInOpts) {
    try {
      const user = await Auth.signIn(formData);
      capture.setUser({ email: user.attributes.email, id: user.attributes.sub });
      Analytics.identify(user.attributes.sub);
      Analytics.emit(Actions.signIn, Features.user);
      return user;
    } catch (err) {
      capture.error(err, { extra: { username: formData.username, context: `signin` } });
      throw err;
    }
  },
  async handleSignUp(formData: SignUpParams) {
    try {
      const user = await Auth.signUp({
        ...formData,
        autoSignIn: {
          enabled: true,
        },
      });
      if (isDevEnv()) {
        //eslint-disable-next-line @typescript-eslint/no-explicit-any
        const attrib = formData.attributes as any; // safe, for dev purposes only
        await developmentInitAccount({
          id: user.userSub,
          email: formData.username,
          firstName: attrib.given_name,
          lastName: attrib.family_name,
          website: attrib.website,
        });
      }
      capture.setUser({ email: formData.username, id: user.userSub });
      Analytics.identify(user.userSub);
      Analytics.emit(Actions.signUp, Features.user);
      return user;
    } catch (err) {
      capture.error(err, { extra: { username: formData.username, context: `signup` } });
      throw err;
    }
  },
};

// https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/#usage-with-react-router-64-data-api
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export const App = () => {
  return (
    <ThemeProvider theme={amplifyTheme}>
      <Authenticator
        components={components}
        services={services}
        signUpAttributes={["given_name", "family_name", "website"]}
        loginMechanisms={["email"]}
      >
        {({ signOut, user }) => (
          <main>
            <BrowserRouter>
              <ChakraProvider theme={theme}>
                <AppStateProvider>
                  <Navbar
                    children={
                      <SentryRoutes>
                        <Route path="/" element={<Home></Home>} />
                        <Route path="developers" element={<Settings></Settings>} />
                        <Route path="medical" element={<Medical></Medical>} />
                        <Route path="billing" element={<Billing></Billing>} />
                        <Route
                          path="subscription-successful"
                          element={<SubcriptionSuccess></SubcriptionSuccess>}
                        />
                      </SentryRoutes>
                    }
                    signOut={signOut}
                    user={user}
                  />
                </AppStateProvider>
              </ChakraProvider>
            </BrowserRouter>
          </main>
        )}
      </Authenticator>
    </ThemeProvider>
  );
};
